import { useState } from "react";
import { usePageMeta } from "../usePageMeta";
import { StitchSimulator } from "../components/StitchSimulator";

interface ReplayPageProps {
  apiUrl: string;
  token: string;
  // Exactly one of these is set, by the two /replay routes in App.tsx.
  assetId?: string;
  contentKey?: string;
  shareButtonsEnabled: boolean;
  emailCaptureEnabled: boolean;
  proUpsellVariant: string;
}

/**
 * The dedicated view for one design's simulation, linked to from a "Watch it
 * stitch" result on /create or /convert. The other view of the simulator,
 * /replay itself (ReplayUploadPage), shows the same StitchSimulator inline
 * next to its upload form instead of linking here -- this page exists for
 * the case where a specific design's simulation is worth its own URL to
 * share or return to.
 */
export function ReplayPage({
  apiUrl,
  token,
  assetId,
  contentKey,
  shareButtonsEnabled,
  emailCaptureEnabled,
  proUpsellVariant,
}: ReplayPageProps) {
  // Filled in once StitchSimulator's fetch resolves, so a generated design's
  // title can name it (an upload has no text/font to name it with).
  const [loaded, setLoaded] = useState<{ text?: string; font?: string; share_image?: string | null }>({});

  usePageMeta({
    title: loaded.text
      ? `Stitch Simulator: "${loaded.text}" — Embroidery Online`
      : "Stitch Simulator — Embroidery Online",
    description:
      "Watch an embroidery design sew, stitch by stitch, the way it would on a real machine.",
    path: assetId ? `/replay/${assetId}` : `/replay/content/${contentKey}`,
    // An invitation, not a page description -- what shows in the link
    // preview when someone shares this design, not what a search result or
    // browser tab shows. Built from window.location.origin, not
    // usePageMeta's own SITE_ORIGIN: dev and prod are separate databases, so
    // a design's image only actually exists on whichever host generated it
    // (see the same reasoning in ShareButton.tsx).
    ogTitle: "Check out this embroidery design",
    ogDescription: loaded.text
      ? `"${loaded.text}" stitched out, watched stitch by stitch.`
      : "Watch this embroidery design sew, stitch by stitch.",
    // share_image, not the raw design PNG -- see build_share_card in
    // app/services/media.py for why a design's own PNG is the wrong image to
    // link here (its aspect ratio is exactly what makes a link preview
    // render as a tiny illegible crop instead of a full-size card).
    image: loaded.share_image ? `${window.location.origin}${loaded.share_image}` : undefined,
  });

  return (
    <>
      <section className="tool-intro">
        <h1>Stitch Simulator</h1>
        <p>
          Replays a design stitch by stitch, needle jumps and color changes
          included -- the same order it would sew in on a real machine.
        </p>
      </section>

      <StitchSimulator
        apiUrl={apiUrl}
        assetId={assetId}
        contentKey={contentKey}
        onLoaded={setLoaded}
        showDownloads={shareButtonsEnabled}
        token={token}
        emailCaptureEnabled={emailCaptureEnabled}
        proUpsellVariant={proUpsellVariant}
        emailSource="replay"
      />
    </>
  );
}
